// ------------------------------------------------------------------------------
// IMPORTS
// ------------------------------------------------------------------------------
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Form, Controller } from "react-hook-form";

import RegisterNewUserService from "../../Services/RegisterNewUserService";

import { StyledTextField } from "./Common/TextField";
import { commonStyles } from "./Common/CommonStyles";
import StyledButton from "../NurtureProspects/ReusableComponents/Button";
import { StyledLink } from "../NurtureProspects/ReusableComponents/StyledLink";

import {
  Box,
  CircularProgress,
  FormLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { MdErrorOutline } from "react-icons/md";
import { MdLightbulbOutline } from "react-icons/md";

// ------------------------------------------------------------------------------
// STYLES
// ------------------------------------------------------------------------------
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "48px",

    "& h1": {
      fontSize: "32px",
      fontWeight: "600",
      fontFamily: "Inter, sans-serif",
      color: "rgba(65, 80, 88, 1)",
      margin: "0",
    },
  },
  suggestionbox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "400",
    color: "#053a64",
    border: "1px solid rgba(196, 221, 241, 1)",
    background: "rgba(10, 114, 195, 0.05)",
    borderRadius: "8px",
    alignSelf: "stretch",
    fontFamily: "Inter, sans-serif",

    "& span": {
      textAlign: "left",
    },

    "& svg": {
      height: "24px",
      width: "24px",
      color: "rgba(10, 114, 195, 1)",
    },
  },
  form: {
    minWidth: "430px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
};

// ------------------------------------------------------------------------------
// MAIN
// ------------------------------------------------------------------------------

function CreateAccount() {
  const theme = useTheme();
  const smallDisplay = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // -------------------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(false);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [emailError, setEmailError] = useState(null);

  // -------------------------------------------------------------------------------------
  // React Hook Form
  // -------------------------------------------------------------------------------------
  
  const formDefaultValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  };

  const {
    control: formController,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: formDefaultValues,
    values: formDefaultValues,
  });

  // -------------------------------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------------------------------
  
  const handleCreateAccount = async (data) => {
    try {
      setIsLoading(true);
      setSubmitResponse(null);
      setEmailError(null);

      let group = "Engage AI";
      const queryParams = new URLSearchParams(window.location.search);
      const source = queryParams.get("source");
      source === "engageai" && (group = "Engage AI");

      const { firstName, lastName, email, password } = data;

      const response = await RegisterNewUserService(
        firstName,
        lastName,
        email,
        password,
        group
      );
      const json = await response.json();

      if (response.status === 200) {
        navigate("/onboarding/EmailVerification", { state: { email } });
      } else {
        if (json?.message === "Account already exists")
          setEmailError("This email already exists.");
        else throw new Error(json?.message);
      }
    } catch (err) {
      setSubmitResponse("Something went wrong. Please contact support.");
    } finally {
      setIsLoading(false);
    }
  }; 

  // -------------------------------------------------------------------------------------
  // COMPONENT RENDERING
  // -------------------------------------------------------------------------------------

  return (
    <Box sx={styles.container}>
      {/**<Box display="flex" flexDirection="column" alignItems="center" gap="16px">
        <p>We are no longer accepting new registrations as Engage AI is ceasing operation 😔</p>
        
      </Box>**/}
      <Form
        control={formController}
        onSubmit={handleSubmit(handleCreateAccount)}
      >
        <Box sx={{ ...styles.form, ...(smallDisplay && { minWidth: "100%" }) }}>
          <Box sx={styles.suggestionbox}>
            <MdLightbulbOutline />
            <span>
              We recommend using your <strong>work email</strong> - it keeps
              work and life separate.
            </span>
          </Box>
          {!!submitResponse && (
            <Box sx={{ ...commonStyles.globalError, maxWidth: "530px" }}>
              <MdErrorOutline />
              <span>{submitResponse}</span>
            </Box>
          )}

          <Box
            display="flex"
            flexDirection={smallDisplay ? "column" : "row"}
            justifyContent="space-between"
            gap="16px"
          >
            <Box sx={commonStyles.formGroup}>
              <FormLabel htmlFor="firstName">First name</FormLabel>
              <Controller
                name="firstName"
                control={formController}
                rules={{ required: "required." }}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    id="firstName"
                    variant="outlined"
                    placeholder="First Name"
                    error={!!formErrors?.firstName}
                    sx={{
                      ...(!!formErrors?.firstName && {
                        "& input": {
                          color: "var(--color-red)",
                          borderColor: "var(--color-red)",
                        },
                      }),
                    }}
                  />
                )}
              />
              {!!formErrors?.firstName && (
                <p style={{ ...commonStyles.errMsg, alignSelf: "flex-end" }}>
                  {formErrors?.firstName?.message}
                </p>
              )}
            </Box>
            <Box sx={commonStyles.formGroup}>
              <FormLabel htmlFor="lastName">Last name</FormLabel>
              <Controller
                name="lastName"
                control={formController}
                rules={{ required: "required." }}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    id="lastName"
                    variant="outlined"
                    placeholder="Last Name"
                    error={!!formErrors?.lastName}
                    sx={{
                      ...(!!formErrors?.lastName && {
                        "& input": {
                          color: "var(--color-red)",
                          borderColor: "var(--color-red)",
                        },
                      }),
                    }}
                  />
                )}
              />
              {!!formErrors?.lastName && (
                <p style={{ ...commonStyles.errMsg, alignSelf: "flex-end" }}>
                  {formErrors?.lastName?.message}
                </p>
              )}
            </Box>
          </Box>
          <Box sx={commonStyles.formGroup}>
            <FormLabel htmlFor="email">E-mail</FormLabel>
            <Controller
              id="email"
              name="email"
              control={formController}
              rules={{
                required: "required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
                  message: "Invalid email address",
                },
                validate: (value) =>
                  !/\+/.test(value) || "Invalid email address",
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  id="email"
                  variant="outlined"
                  placeholder="Email"
                  error={!!formErrors?.email || !!emailError}
                  autoComplete="off"
                  sx={{
                    ...((!!formErrors?.email || emailError) && {
                      "& input": {
                        color: "var(--color-red)",
                        borderColor: "var(--color-red)",
                      },
                    }),
                  }}
                />
              )}
            />
            {(!!formErrors?.email || emailError) && (
              <p style={{ ...commonStyles.errMsg, alignSelf: "flex-end" }}>
                {emailError ? emailError : formErrors?.email?.message}
              </p>
            )}
          </Box>
          <Box sx={commonStyles.formGroup}>
            <FormLabel htmlFor="password">Password</FormLabel>
            <Controller
              id="password"
              name="password"
              control={formController}
              rules={{
                required: "required.",
                minLength: {
                  value: 8,
                  message: "Use at least 8 characters.",
                },
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  id="password"
                  variant="outlined"
                  placeholder="Password"
                  type="password"
                  error={!!formErrors?.password}
                  autoComplete="new-password"
                  sx={{
                    ...(!!formErrors?.password && {
                      "& input": {
                        color: "var(--color-red)",
                        borderColor: "var(--color-red)",
                      },
                    }),
                  }}
                />
              )}
            />
            {!!formErrors?.password && (
              <p style={{ ...commonStyles.errMsg, alignSelf: "flex-end" }}>
                {formErrors?.password?.message}
              </p>
            )}
          </Box>
          <Box sx={commonStyles.actions}>
            <StyledButton
              disabled={isLoading}
              sx={{
                "& span": {
                  color: "white !important",
                  width: "24px !important",
                  height: "24px !important",
                },
              }}
              type="submit"
            >
              {isLoading ? <CircularProgress /> : "Sign up"}
            </StyledButton>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            sx={{
              mt: "14px",
              fontSize: "14px",
              fontWeight: 500,
              color: "rgba(65, 80, 88, 1)",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <span>Already have an account?</span>
            <StyledLink to="/Login">Log in</StyledLink>
          </Box>
        </Box>
      </Form>
    </Box>
  );
}

export default CreateAccount;
